import React, { useEffect } from 'react'
import { useLocation, useHistory, useParams } from 'react-router-dom'
import I18n from 'i18n'
import WeeklyQuestionnaireDashboard from './WeeklyQuestionnaireDashboard'
import DateRangeQuestionnaireDashboard from './DateRangeQuestionnaireDashboard'
import DashboardStateWrapper, { DashboardWrapperProps } from '../common/DashboardStateWrapperTS'
import SpinnerWrapper from 'components/common/SpinnerWrapper'
import Profile from 'components/common/types/Profile'

// This class is a tab picker for the welness dashboard. To differentiate
// between weekly and monthly, and go to a specific athlete. So it can be
// navigated from load tab.

const TABS = ['wekelijks', 'maandelijks'] as const
type Tab = typeof TABS[number]
const DEFAULT_TAB: Tab = 'wekelijks'

const getTabFromHash = (hash: string): Tab => {
  const tabName = (hash !== '' ? hash : '').replace(/^#/, '') as Tab
  return TABS.includes(tabName) ? tabName : DEFAULT_TAB
}

interface OwnProps {
  sessionToken: string
  myProfile: Profile
}

type Props = OwnProps & DashboardWrapperProps

const WellbeingTabPicker: React.FC<Props> = (props: Props) => {
  const location = useLocation()
  const history = useHistory()
  const params = useParams<{ id?: string }>()

  // URL to UI
  const activeTab = getTabFromHash(location.hash)
  const rawId = typeof params.id === 'string' ? params.id.trim() : ''
  const athleteId =
  rawId === '' ? undefined : (Number.isNaN(Number.parseInt(rawId, 10)) ? undefined : Number.parseInt(rawId, 10))

  // UI to URL
  const onTabChange = (nextTab: Tab): void => {
    history.push({
      pathname: location.pathname,
      search: location.search,
      hash: nextTab
    })
  }

  useEffect(() => {
    if (location.hash === '') {
      history.replace({
        pathname: location.pathname,
        search: location.search,
        hash: DEFAULT_TAB
      })
    }
  }, [location.hash, location.pathname, location.search, history])

  // Initialize materialize tabs.
  useEffect(() => {
    const resetTabs = setTimeout(() => {
      const elems = document.querySelectorAll('.tabs')
      if (elems.length > 0 && typeof (window as any).M !== 'undefined' && (window as any).M !== null) {
        (window as any).M.Tabs.init(elems)
      }
    }, 0)
    return () => {
      clearTimeout(resetTabs)
    }
  }, [activeTab])

  return (
    <SpinnerWrapper ready transparent={false}>
      <div className='row'>
        <div className='col s12'>
          <ul className='tabs'>
            <li className='tab col s6'>
              <a
                href='#wekelijks'
                className={activeTab === 'wekelijks' ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault()
                  onTabChange('wekelijks')
                }}
              >
                {I18n.t('components.dashboards.questionnaire.weekly.tab_title')}
              </a>
            </li>
            <li className='tab col s6'>
              <a
                href='#maandelijks'
                className={activeTab === 'maandelijks' ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault()
                  onTabChange('maandelijks')
                }}
              >
                {I18n.t('components.dashboards.questionnaire.monthly.tab_title')}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className='row'>
        <div className='col s12'>
          {activeTab === 'wekelijks' && (
            <div id='wekelijks-tab' className='active'>
              <WeeklyQuestionnaireDashboard athleteId={athleteId} />
            </div>
          )}
          {activeTab === 'maandelijks' && (
            <div id='maandelijks-tab' className='active'>
              <DateRangeQuestionnaireDashboard {...props} />
            </div>
          )}
        </div>
      </div>
    </SpinnerWrapper>
  )
}

export default DashboardStateWrapper(WellbeingTabPicker)
